import React from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Building2, Phone, Loader2, CheckCircle2, Info } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY } from "../lib/company";
import useSeo from "../hooks/useSeo";

const CATEGORIES = [
  { key: "kundeservice", label: "Kundeservice — kundespørgsmål", inbox: "kundeservice@emakurer.dk" },
  { key: "klage", label: "Klage", inbox: "kundeservice@emakurer.dk" },
  { key: "tracking", label: "Spørgsmål om tracking", inbox: "kundeservice@emakurer.dk" },
  { key: "booking", label: "Problem med booking", inbox: "kundeservice@emakurer.dk" },
  { key: "levering", label: "Leveringsproblem", inbox: "kundeservice@emakurer.dk" },
  { key: "erhverv", label: "Erhverv / B2B-aftale", inbox: COMPANY.email_business },
  { key: "job", label: "Job / chauffør-ansøgning", inbox: COMPANY.email_jobs },
  { key: "bogholderi", label: "Bogholderi / faktura / betaling", inbox: COMPANY.email_billing },
  { key: "generel", label: "Generel henvendelse / andet", inbox: COMPANY.email_general },
];

export default function ContactPage() {
  useSeo({
    title: "Kontakt EMA Kurer ApS · Lokal kurér og B2B-logistik på Sjælland",
    description: `Kontakt EMA Kurer ApS — kurérservice og B2B-platform på Sjælland. Telefon ${COMPANY.phone}, ${COMPANY.email_general}, ${COMPANY.email_billing}. CVR ${COMPANY.cvr}.`,
    path: "/kontakt",
  });
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "", subject: "", message: "", category: "kundeservice",
  });
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(null); // {reference, routed_to, category}

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedCat = CATEGORIES.find((c) => c.key === form.category) || CATEGORIES[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Udfyld alle obligatoriske felter"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(data);
      toast.success("Tak! Vi har modtaget din henvendelse.");
    } catch {
      toast.error("Noget gik galt — prøv igen.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-brand-dark text-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Kontakt</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05]">
            Vi er her, når du har brug for os.
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="kicker">Kontaktoplysninger</div>
          <h2 className="font-display font-semibold text-3xl mt-3 text-brand-dark tracking-tight">EMA Kurer ApS</h2>

          <div className="mt-7 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-brand-red text-white flex items-center justify-center"><Phone size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-text2">Telefon</div>
                <a href={`tel:${COMPANY.phone_e164}`} data-testid="contact-phone" className="font-display font-semibold text-brand-dark mt-0.5 block">{COMPANY.phone}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-brand-dark text-white flex items-center justify-center"><Mail size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-text2">Generel henvendelse</div>
                <a href={`mailto:${COMPANY.email_general}`} data-testid="contact-email" className="font-display font-semibold text-brand-dark mt-0.5 block">{COMPANY.email_general}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-brand-dark text-white flex items-center justify-center"><Building2 size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-text2">Bogholderi</div>
                <a href={`mailto:${COMPANY.email_billing}`} data-testid="contact-acct" className="font-display font-semibold text-brand-dark mt-0.5 block">{COMPANY.email_billing}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-brand-dark text-white flex items-center justify-center"><MapPin size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-text2">Område</div>
                <div className="font-display font-semibold text-brand-dark mt-0.5">{COMPANY.coverage} · CVR {COMPANY.cvr}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 border border-brand">
            <iframe
              title="Sjælland kort"
              data-testid="map-embed"
              src="https://www.google.com/maps?q=Zealand+Denmark&output=embed"
              width="100%" height="280" loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0, display: "block" }}
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <div className="bg-white border border-brand p-10 text-center" data-testid="contact-sent">
              <div className="w-14 h-14 bg-brand-red text-white mx-auto flex items-center justify-center"><CheckCircle2 size={28} /></div>
              <h2 className="font-display font-bold text-3xl text-brand-dark mt-6">Tak for din besked!</h2>
              <p className="text-brand-text2 mt-3">Vi vender tilbage hurtigst muligt på den angivne email.</p>
              <div className="mt-6 bg-brand-bg2 border border-brand p-5 text-left">
                <div className="kicker mb-2">Reference-nummer</div>
                <code className="font-mono text-lg text-brand-red font-bold" data-testid="contact-ref">{sent.reference}</code>
                <div className="mt-3 text-xs text-brand-text2">
                  Behandles af <b className="text-brand-dark">{sent.routed_to}</b> · Kategori: {sent.category}
                </div>
                <p className="text-xs text-brand-text2 mt-3">
                  Du modtager også en bekræftelses-mail med dit reference-nummer. Gem det hvis du følger op.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-brand p-7 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
              <div className="sm:col-span-2">
                <div className="kicker">Skriv til os</div>
                <div className="font-display font-semibold text-2xl text-brand-dark mt-2">Send en besked</div>
              </div>
              <div className="sm:col-span-2">
                <label className="label-flat">Hvad drejer det sig om? *</label>
                <select
                  className="input-flat bg-white"
                  value={form.category}
                  onChange={update("category")}
                  data-testid="contact-category"
                  required
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
                <div className="flex items-start gap-2 mt-2 text-xs text-brand-text2">
                  <Info size={13} className="mt-0.5 flex-shrink-0" />
                  <span>Sendes automatisk til <b className="text-brand-dark">{selectedCat.inbox}</b> for hurtigst muligt svar</span>
                </div>
              </div>
              <div>
                <label className="label-flat">Navn *</label>
                <input className="input-flat" value={form.name} onChange={update("name")} data-testid="contact-name" required />
              </div>
              <div>
                <label className="label-flat">Email *</label>
                <input type="email" className="input-flat" value={form.email} onChange={update("email")} data-testid="contact-input-email" required />
              </div>
              <div>
                <label className="label-flat">Telefon</label>
                <input className="input-flat" value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
              </div>
              <div>
                <label className="label-flat">Emne *</label>
                <input className="input-flat" value={form.subject} onChange={update("subject")} data-testid="contact-subject" required />
              </div>
              <div className="sm:col-span-2">
                <label className="label-flat">Besked *</label>
                <textarea rows={5} className="input-flat" value={form.message} onChange={update("message")} data-testid="contact-message" required />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" className="btn-primary" disabled={busy} data-testid="contact-submit">
                  {busy ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : <>Send besked <ArrowRight size={16} /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
